import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MediaInteractionModal from '../components/MediaInteractionModal';
import api from '../utils/api';
import { getPublicAuditionShareUrl, shareLink } from '../utils/share';
import './Auditions.css';
import './UserSearch.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const getMediaUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${BACKEND_URL}/uploads/${key}`;
};

const ROLE_OPTIONS = [
  'Actor', 'Director', 'Writer', 'Cameraman', 'Editor', 'Producer',
  'Cinematographer', 'Sound Designer', 'Music Composer', 'Costume Designer',
  'Makeup Artist', 'Production Designer', 'Art Director', 'VFX Artist',
  'Stunt Coordinator', 'Casting Director', 'Line Producer', 'Assistant Director',
  'Gaffer', 'Grip',
];

interface User {
  id: number;
  name: string;
  email: string;
  city?: string;
  state?: string;
  roles?: string[];
  profile_completed?: boolean;
  connectionStatus?: string | null;
  connectionInitiatedBy?: number | null;
  invitationStatus: string | null;
  groupAssignments?: Array<{
    role_type: string;
    role_name: string;
    role_description?: string;
    status: string;
  }>;
}

interface Character {
  character_name: string;
}

interface ConfirmState {
  open: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface PublicAuditionRecord {
  id: number;
  story_id: number;
  storyline: string;
  character_line: string;
  description: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

type RoleType = 'character' | 'crew' | 'extra';
type AuditionTab = 'choose' | 'public';
type AuditionPreviewType = 'video' | 'photo' | 'pdf';

const isPdfAuditionAsset = (mediaType?: string, fileName?: string | null, url?: string | null) => {
  const target = `${fileName || ''} ${url || ''}`.toLowerCase();
  return mediaType === 'pdf' || target.includes('.pdf');
};

const getAuditionPreviewType = (mediaType?: string, fileName?: string | null, url?: string | null): AuditionPreviewType => {
  if (isPdfAuditionAsset(mediaType, fileName, url)) return 'pdf';
  return mediaType === 'photo' ? 'photo' : 'video';
};

const getDownloadFileName = (headers: any, fallback?: string) => {
  const disposition = String(headers?.['content-disposition'] || '');
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback || 'response';
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

// ── UserProfileModal interfaces ──────────────────────────────

interface MediaItem {
  id: number;
  file_name: string;
  media_type: string;
  is_collab?: boolean;
  collaborator_role?: string | null;
  owner_name?: string | null;
}

interface Collaborator {
  id: number;
  media_id: number;
  role_name: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  user_roles: string[];
  user_city: string | null;
  user_state: string | null;
}

interface UserProfile {
  user: {
    id: number;
    name: string;
    email: string;
    city?: string;
    state?: string;
    roles?: string[];
    profile_picture?: string;
  };
  connectionStatus: string | null;
  connectionInitiatedBy: number | null;
  connectionCount?: number;
  followerCount?: number;
  photos: MediaItem[];
  videos: MediaItem[];
}

interface UserProfileModalProps {
  userId: number;
  onClose: () => void;
  onConnect: (userId: number) => void | Promise<void>;
  onDisconnect: (userId: number) => void | Promise<void>;
  onStartChat: (userId: number) => void;
}

const sortUsersByProfileCompletion = (list: User[]) =>
  [...list].sort((a, b) => {
    const aCompleted = a.profile_completed !== false;
    const bCompleted = b.profile_completed !== false;

    if (aCompleted !== bCompleted) {
      return aCompleted ? -1 : 1;
    }

    return a.name.localeCompare(b.name);
  });

// ── Response inline-invite state ─────────────────────────────

interface ResponseInviteState {
  responseId: number;
  userId: number;
  userName: string;
  roleType: RoleType;
  selectedCharacter: string;
  crewDescription: string;
  inviteSent: boolean;
  loading: boolean;
}

// ── UserProfileModal component ───────────────────────────────

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  userId,
  onClose,
  onConnect,
  onDisconnect,
  onStartChat,
}) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [viewMediaType, setViewMediaType] = useState<'photos' | 'videos'>('photos');
  const [loading, setLoading] = useState(true);
  const [dpError, setDpError] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [lightbox, setLightbox] = useState<{
    url: string;
    isVideo: boolean;
    mediaId: number;
  } | null>(null);

  const [collabMap, setCollabMap] = useState<Record<number, Collaborator[]>>({});
  const [collabLoading, setCollabLoading] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setLoading(true);
    setDpError(false);
    setCollabMap({});
    api
      .get(`/user/${userId}/profile`)
      .then((res) => setProfile(res.data))
      .catch((err) => console.error('Failed to load profile:', err))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightbox) setLightbox(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, onClose]);

  const fetchCollabs = useCallback(
    async (mediaId: number) => {
      if (collabMap[mediaId] !== undefined) return;
      setCollabLoading((prev) => ({ ...prev, [mediaId]: true }));
      try {
        const res = await api.get(`/media/${mediaId}/collaborators`);
        setCollabMap((prev) => ({ ...prev, [mediaId]: res.data.collaborators }));
      } catch {
        setCollabMap((prev) => ({ ...prev, [mediaId]: [] }));
      } finally {
        setCollabLoading((prev) => ({ ...prev, [mediaId]: false }));
      }
    },
    [collabMap]
  );

  const openLightbox = (url: string, isVideo: boolean, mediaId: number) => {
    setLightbox({ url, isVideo, mediaId });
    fetchCollabs(mediaId);
  };

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    try {
      await onDisconnect(userId);
      setProfile((current) =>
        current
          ? {
              ...current,
              connectionStatus: null,
              connectionInitiatedBy: null,
            }
          : current,
      );
    } finally {
      setDisconnecting(false);
    }
  }, [onDisconnect, userId]);

  if (loading || !profile) {
    return (
      <div className="us-overlay" onClick={onClose}>
        <div className="us-modal" onClick={(e) => e.stopPropagation()}>
          <div className="us-loading" style={{ minHeight: 'unset', padding: '48px 0' }}>
            Loading…
          </div>
        </div>
      </div>
    );
  }

  const dpUrl = getMediaUrl(profile.user.profile_picture);
  const photos = profile.photos || [];
  const videos = profile.videos || [];
  const activeMedia = viewMediaType === 'photos' ? photos : videos;

  return (
    <>
      <div className="us-overlay" onClick={onClose}>
        <div className="us-modal" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="us-modal-close">
            ✕
          </button>

          <div className="us-modal-header">
            <div
              className={`us-modal-avatar${dpUrl && !dpError ? ' clickable' : ''}`}
              onClick={() => dpUrl && !dpError && openLightbox(dpUrl, false, -1)}
            >
              {dpUrl && !dpError ? (
                <img
                  src={dpUrl}
                  alt={profile.user.name}
                  onError={() => setDpError(true)}
                />
              ) : (
                profile.user.name.charAt(0).toUpperCase()
              )}
            </div>
            <h2>{profile.user.name}</h2>
            <p className="us-modal-email">{profile.user.email}</p>
            <div className="us-modal-stats">
              <span><strong>{profile.connectionCount ?? 0}</strong> Connections</span>
              <span><strong>{profile.followerCount ?? 0}</strong> Followers</span>
            </div>
          </div>

          <div className="us-modal-details">
            {profile.user.roles && profile.user.roles.length > 0 && (
              <div className="us-modal-detail-row">
                <span className="us-modal-detail-label">Roles</span>
                <div className="us-modal-detail-value us-modal-roles">
                  {profile.user.roles.map((role, i) => (
                    <span key={i} className="us-role-tag">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {profile.user.city && (
              <div className="us-modal-detail-row">
                <span className="us-modal-detail-label">Location</span>
                <span className="us-modal-detail-value">
                  {profile.user.city}, {profile.user.state}
                </span>
              </div>
            )}
          </div>

          {(photos.length > 0 || videos.length > 0) && (
            <div className="us-modal-gallery">
              <div className="us-modal-tabs">
                <button
                  className={`us-modal-tab ${viewMediaType === 'photos' ? 'active' : ''}`}
                  onClick={() => setViewMediaType('photos')}
                >
                  Photos ({photos.length})
                </button>
                <button
                  className={`us-modal-tab ${viewMediaType === 'videos' ? 'active' : ''}`}
                  onClick={() => setViewMediaType('videos')}
                >
                  Videos ({videos.length})
                </button>
              </div>

              {activeMedia.length > 0 ? (
                <div className="us-modal-media-grid">
                  {activeMedia.map((item) => {
                    const url = getMediaUrl(item.file_name);
                    if (!url) return null;
                    const isVideo = item.media_type === 'video';
                    return (
                      <div
                        key={item.id}
                        className={`us-modal-media-item${
                          isVideo ? ' us-modal-media-item--video' : ''
                        }`}
                        onClick={() => openLightbox(url, isVideo, item.id)}
                      >
                        {isVideo ? (
                          <>
                            <video src={url} preload="metadata" />
                            <span className="us-play-icon">▶</span>
                          </>
                        ) : (
                          <img
                            src={url}
                            alt="Photo"
                            onError={(e) => {
                              e.currentTarget.parentElement!.style.display = 'none';
                            }}
                          />
                        )}
                        {item.is_collab && (
                          <span className="us-collab-media-badge">
                            Tagged as {item.collaborator_role || 'Collaborator'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="us-modal-no-media">
                  <p>No {viewMediaType} yet.</p>
                </div>
              )}
            </div>
          )}

          <div className="us-modal-actions">
            {!profile.connectionStatus && (
              <button
                onClick={() => {
                  onConnect(userId);
                  onClose();
                }}
                className="us-btn us-btn--filled us-btn--full"
              >
                Connect
              </button>
            )}
            {profile.connectionStatus === 'pending' && (
              <span className="us-status-badge us-status-badge--full">
                Request Pending
              </span>
            )}
            {profile.connectionStatus === 'accepted' && (
              <div className="us-modal-action-stack">
                <button
                  onClick={() => {
                    onStartChat(userId);
                    onClose();
                  }}
                  className="us-btn us-btn--filled us-btn--full"
                >
                  Start Chat
                </button>
                <button
                  onClick={() => void handleDisconnect()}
                  className="us-btn us-btn--ghost us-btn--full"
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            )}
            {false && profile?.connectionStatus === 'accepted' && (
              <button
                onClick={() => {
                  onStartChat(userId);
                  onClose();
                }}
                className="us-btn us-btn--filled us-btn--full"
              >
                💬 Start Chat
              </button>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <MediaInteractionModal
          mediaId={lightbox.mediaId}
          src={lightbox.url}
          isVideo={lightbox.isVideo}
          collaborators={(collabMap[lightbox.mediaId] || []).map(c => ({
            ...c,
            user_dp: getMediaUrl(c.user_dp),
          }))}
          collaboratorsLoading={Boolean(collabLoading[lightbox.mediaId])}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

// ── Main page ────────────────────────────────────────────────

const AuditionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();
  const [activeTab, setActiveTab] = useState<AuditionTab>('choose');

  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Choose-members role modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [roleType, setRoleType] = useState<RoleType>('character');
  const [selectedCharacter, setSelectedCharacter] = useState('');
  const [crewDescription, setCrewDescription] = useState('');
  const [inviteSent, setInviteSent] = useState(false);

  // Custom confirm dialog
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false,
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });

  // Profile modal
  const [profileModalUserId, setProfileModalUserId] = useState<number | null>(null);

  // ── Public audition state ──────────────────────────────────
  const [allPublicAuditions, setAllPublicAuditions] = useState<PublicAuditionRecord[]>([]);
  const [selectedAuditionId, setSelectedAuditionId] = useState<number | null>(null);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [publicAudition, setPublicAudition] = useState<PublicAuditionRecord | null>(null);
  const [publicMedia, setPublicMedia] = useState<any[]>([]);
  const [publicResponses, setPublicResponses] = useState<any[]>([]);
  const [storyline, setStoryline] = useState('');
  const [characterLine, setCharacterLine] = useState('');
  const [description, setDescription] = useState('');
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicMessage, setPublicMessage] = useState('');
  const [isOwner, setIsOwner] = useState(false);
  const [isUploadingPublicMedia, setIsUploadingPublicMedia] = useState(false);

  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editStoryline, setEditStoryline] = useState('');
  const [editCharacterLine, setEditCharacterLine] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Media preview modal
  const [mediaModal, setMediaModal] = useState<{
    isOpen: boolean;
    url: string;
    type: AuditionPreviewType;
    fileName?: string;
    mediaId?: number;
    responseId?: number;
  }>({ isOpen: false, url: '', type: 'video' });
  const refreshingPublicMediaRef = useRef<Set<number>>(new Set());
  const refreshingResponseMediaRef = useRef<Set<number>>(new Set());

  // ── Response inline invite state ───────────────────────────
  const [responseInvites, setResponseInvites] = useState<
    Record<number, ResponseInviteState>
  >({});

  useEffect(() => {
    loadData();
  }, [storyId]);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedRoles, users]);

  useEffect(() => {
    if (activeTab === 'public') loadAllPublicAuditions();
  }, [activeTab]);

  // ── Helpers ───────────────────────────────────────────────

  const showConfirm = (msg: string): Promise<boolean> =>
    new Promise((resolve) => {
      setConfirm({
        open: true,
        message: msg,
        onConfirm: () => {
          setConfirm((c) => ({ ...c, open: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirm((c) => ({ ...c, open: false }));
          resolve(false);
        },
        });
    });

  const resetPublicComposer = () => {
    setSelectedAuditionId(null);
    setDraftId(null);
    setPublicAudition(null);
    setPublicMedia([]);
    setPublicResponses([]);
    setStoryline('');
    setCharacterLine('');
    setDescription('');
    setIsEditMode(false);
    setResponseInvites({});
  };

  const flashError = (msg: string) => {
    setError(msg);
    setTimeout(() => setError(''), 2000);
  };
  const flashMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 2000);
  };

  // ── Data loading ──────────────────────────────────────────

  const loadData = async () => {
    setLoading(true);
    try {
      const rolesParam =
        selectedRoles.length > 0 ? `?roles=${selectedRoles.join(',')}` : '';
      const [usersRes, charsRes] = await Promise.all([
        api.get(`/auditions/${storyId}/users${rolesParam}`),
        api.get(`/auditions/${storyId}/characters`),
      ]);
      const sortedUsers = sortUsersByProfileCompletion(usersRes.data.users || []);
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
      setCharacters(charsRes.data.characters);
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAllPublicAuditions = async () => {
    setPublicLoading(true);
    try {
      const res = await api.get(`/public-auditions/story/${storyId}/all`);
      const auditions = res.data.auditions || [];
      setIsOwner(res.data.isOwner || false);
      setAllPublicAuditions(auditions);
      const activeId = selectedAuditionId ?? draftId;
      if (activeId !== null) {
        const exists = auditions.some((audition: PublicAuditionRecord) => audition.id === activeId);
        if (!exists) {
          resetPublicComposer();
        }
      }
    } catch (err) {
      console.error('Failed to load public auditions:', err);
      flashError('Failed to load auditions');
    } finally {
      setPublicLoading(false);
    }
  };

  const loadSingleAudition = async (auditionId: number) => {
    setPublicLoading(true);
    try {
      const res = await api.get(`/public-auditions/${auditionId}`);
      const audition: PublicAuditionRecord = res.data.audition;
      setPublicAudition(audition);
      setPublicMedia(res.data.media || []);
      setPublicResponses(res.data.responses || []);
      setStoryline(audition.storyline);
      setCharacterLine(audition.character_line);
      setDescription(audition.description);
      setIsEditMode(false);
      setResponseInvites({});
    } catch (err) {
      console.error('Failed to load audition:', err);
      flashError('Failed to load audition details');
    } finally {
      setPublicLoading(false);
    }
  };

  // ── Draft creation ────────────────────────────────────────

  const ensureDraftExists = async () => {
    if (draftId !== null || selectedAuditionId !== null) return;
    try {
      const res = await api.post(
        `/public-auditions/story/${storyId}/draft`
      );
      const id: number = res.data.audition.id;
      setDraftId(id);
    } catch {
      flashError('Failed to initialise draft');
    }
  };

  // ── Dropdown selection ────────────────────────────────────

  const handleSelectAudition = (value: string) => {
    if (value === 'new') {
      setSelectedAuditionId(null);
      setDraftId(null);
      setPublicAudition(null);
      setPublicMedia([]);
      setPublicResponses([]);
      setStoryline('');
      setCharacterLine('');
      setDescription('');
      setIsEditMode(false);
      setResponseInvites({});
    } else {
      const id = Number(value);
      setSelectedAuditionId(id);
      setDraftId(null);
      setIsEditMode(false);
      loadSingleAudition(id);
    }
  };

  // ── Filters ───────────────────────────────────────────────

  const applyFilters = () => {
    let filtered = [...users];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.name.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query)
      );
    }
    if (selectedRoles.length > 0) {
      filtered = filtered.filter(
        (u) => u.roles && u.roles.some((r) => selectedRoles.includes(r))
      );
    }
    setFilteredUsers(sortUsersByProfileCompletion(filtered));
  };

  const handleRoleToggle = (role: string) =>
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );

  const handleClearFilters = () => {
    setSelectedRoles([]);
    setSearchQuery('');
  };

  // ── Choose-members role modal ─────────────────────────────

  const handleAddClick = (user: User) => {
    setSelectedUser(user);
    setShowRoleModal(true);
    setRoleType('character');
    setSelectedCharacter('');
    setCrewDescription('');
    setInviteSent(false);
  };

  const handleSendInvitation = async () => {
    if (!selectedUser) return;
    let roleName = '';
    let roleDescription: string | null = null;

    if (roleType === 'character') {
      if (!selectedCharacter) {
        flashError('Please select a character');
        return;
      }
      roleName = selectedCharacter;
    } else if (roleType === 'crew') {
      if (!crewDescription.trim()) {
        flashError('Please select a crew role');
        return;
      }
      roleName = 'Crew';
      roleDescription = crewDescription;
    } else {
      roleName = 'Extra';
    }

    try {
      await api.post(`/auditions/${storyId}/invite`, {
        receiverId: selectedUser.id,
        roleType,
        roleName,
        roleDescription,
      });

      setSelectedUser((prev) =>
        prev
          ? {
              ...prev,
              groupAssignments: [
                ...(prev.groupAssignments || []),
                {
                  role_type: roleType,
                  role_name: roleName,
                  role_description: roleDescription ?? undefined,
                  status: 'pending',
                },
              ],
            }
          : prev
      );

      setInviteSent(true);
      setTimeout(() => {
        setShowRoleModal(false);
        setInviteSent(false);
        loadData();
      }, 1800);
    } catch (err: any) {
      if (err.response?.data?.receiverProfileIncomplete) {
        flashError('Receiver profile yet to be completed');
        return;
      }
      if (err.response?.data?.requiresConnection) {
        setShowRoleModal(false);
        const userName = selectedUser.name;
        const userId = selectedUser.id;
        const confirmed = await showConfirm(
          `You're not connected with ${userName}. Send a connection request?`
        );
        if (confirmed) {
          try {
            if (!selectedUser.profile_completed) {
              flashError('Receiver profile yet to be completed');
              return;
            }
            await api.post('/connections/request', { targetUserId: userId });
            flashMessage(
              'Connection request sent! Once accepted, you can send the role invitation.'
            );
          } catch (connectErr: any) {
            flashError(
              connectErr.response?.data?.message ||
                'Failed to send connection request'
            );
          }
        }
      } else {
        flashError(
          err.response?.data?.message || 'Failed to send invitation'
        );
      }
    }
  };

  // ── Public audition: Save & Publish ──────────────────────

  const handleSavePublicAudition = async () => {
    if (selectedAuditionId !== null) return;
    if (
      !storyline.trim() ||
      !characterLine.trim() ||
      !description.trim()
    ) {
      flashError('Please fill in all required fields');
      return;
    }

    try {
      let saved: PublicAuditionRecord;

      if (draftId !== null) {
        const res = await api.patch(
          `/public-auditions/${draftId}/publish`,
          {
            storyline,
            character_line: characterLine,
            description,
          }
        );
        saved = res.data.audition;
      } else {
        const res = await api.post(
          `/public-auditions/story/${storyId}`,
          {
            storyline,
            character_line: characterLine,
            description,
          }
        );
        saved = res.data.audition;
      }

      setPublicMessage('Saved and published!');
      setTimeout(() => setPublicMessage(''), 2000);

      setAllPublicAuditions((prev) => {
        const exists = prev.find((a) => a.id === saved.id);
        if (exists) return prev.map((a) => (a.id === saved.id ? saved : a));
        return [...prev, saved];
      });

      setSelectedAuditionId(saved.id);
      setDraftId(null);
      setPublicAudition(saved);
      loadSingleAudition(saved.id);
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to save');
    }
  };

  // ── Public audition: Edit ─────────────────────────────────

  const handleEnterEditMode = () => {
    if (!publicAudition) return;
    setEditStoryline(publicAudition.storyline);
    setEditCharacterLine(publicAudition.character_line);
    setEditDescription(publicAudition.description);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditStoryline('');
    setEditCharacterLine('');
    setEditDescription('');
  };

  const handleSaveEdit = async () => {
    if (!selectedAuditionId) return;
    if (
      !editStoryline.trim() ||
      !editCharacterLine.trim() ||
      !editDescription.trim()
    ) {
      flashError('Please fill in all required fields');
      return;
    }

    setIsSavingEdit(true);
    try {
      const res = await api.patch(
        `/public-auditions/${selectedAuditionId}`,
        {
          storyline: editStoryline,
          character_line: editCharacterLine,
          description: editDescription,
        }
      );

      const updated: PublicAuditionRecord = res.data.audition;

      setPublicAudition(updated);
      setStoryline(updated.storyline);
      setCharacterLine(updated.character_line);
      setDescription(updated.description);

      setAllPublicAuditions((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a))
      );

      setIsEditMode(false);

      // FIX: reload media + responses after edit so publicMedia stays fresh
      await loadSingleAudition(selectedAuditionId);

      setPublicMessage('Audition updated successfully!');
      setTimeout(() => setPublicMessage(''), 2000);
    } catch (err: any) {
      flashError(
        err.response?.data?.message || 'Failed to update audition'
      );
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ── Public audition: Close ────────────────────────────────

  const handleCloseAudition = async () => {
    if (!selectedAuditionId) return;

    const confirmed = await showConfirm(
      'Close this audition? It will no longer appear on the Open Auditions page, but it will stay in your list so you can keep reviewing submissions.'
    );
    if (!confirmed) return;

    try {
      await api.patch(`/public-auditions/${selectedAuditionId}/close`);
      await loadAllPublicAuditions();
      await loadSingleAudition(selectedAuditionId);
      setPublicMessage('Audition closed successfully. It remains available in your list for review.');
      setTimeout(() => setPublicMessage(''), 2000);
    } catch (err: any) {
      flashError(
        err.response?.data?.message || 'Failed to close audition'
      );
    }
  };

  const handleRemoveAudition = async () => {
    const targetId = selectedAuditionId ?? draftId;
    if (!targetId) return;

    const confirmed = await showConfirm(
      'Remove this audition permanently? This will delete the audition and all of its submissions.'
    );
    if (!confirmed) return;

    try {
      await api.delete(`/public-auditions/${targetId}`);
      resetPublicComposer();
      await loadAllPublicAuditions();
      setPublicMessage('Audition removed successfully.');
      setTimeout(() => setPublicMessage(''), 2000);
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to remove audition');
    }
  };

  const handleShareAudition = useCallback(async () => {
    if (!selectedAuditionId || !publicAudition?.is_published) return;

    try {
      const outcome = await shareLink(
        getPublicAuditionShareUrl(selectedAuditionId),
        `${publicAudition.character_line || `Audition #${selectedAuditionId}`} on FilMini`,
      );

      if (outcome === 'copied') {
        flashMessage('Audition link copied to clipboard');
      }
    } catch (shareError) {
      console.error('Failed to share audition:', shareError);
      flashError('Failed to share audition link');
    }
  }, [flashError, flashMessage, publicAudition, selectedAuditionId]);

  // ── Public audition: media upload ─────────────────────────

  const handlePublicMediaUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const oversized = files.find((file) => file.size > 100 * 1024 * 1024);
    if (oversized) {
      flashError(`"${oversized.name}" exceeds the 100MB limit`);
      e.target.value = '';
      return;
    }

    let targetId = selectedAuditionId ?? draftId;
    if (!targetId) {
      try {
        const res = await api.post(
          `/public-auditions/story/${storyId}/draft`
        );
        const id: number = res.data.audition.id;
        setDraftId(id);
        targetId = id;
      } catch {
        flashError('Failed to initialise draft for media upload');
        e.target.value = '';
        return;
      }
    }

    try {
      setIsUploadingPublicMedia(true);
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(
          `/public-auditions/${targetId}/media`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      setPublicMessage(
        files.length === 1 ? 'Media uploaded!' : `${files.length} media items uploaded!`
      );
      setTimeout(() => setPublicMessage(''), 2000);
      await loadSingleAudition(targetId);
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to upload media');
    } finally {
      setIsUploadingPublicMedia(false);
      e.target.value = '';
    }
  };

  const handleDeletePublicMedia = async (mediaId: number) => {
    try {
      await api.delete(
        `/public-auditions/story/${storyId}/media/${mediaId}`
      );
      const targetId = selectedAuditionId ?? draftId;
      if (targetId !== null) loadSingleAudition(targetId);
    } catch {
      flashError('Failed to delete media');
    }
  };

  // ── Response download / delete ────────────────────────────

  const downloadResponseMediaAsset = async (
    responseId: number,
    fileName: string
  ) => {
    const res = await api.get(
      `/public-auditions/${responseId}/response-media?download=1`,
      { responseType: 'blob' }
    );
    downloadBlob(res.data, getDownloadFileName(res.headers, fileName));
  };

  const handleDownloadResponseMedia = async (
    responseId: number,
    fileName: string
  ) => {
    try {
      await downloadResponseMediaAsset(responseId, fileName);
    } catch {
      flashError('Failed to download media');
    }
  };

  const handleDownloadAllResponseMedia = async () => {
    if (publicResponses.length === 0) return;

    try {
      for (const response of publicResponses) {
        await downloadResponseMediaAsset(
          response.id,
          response.file_name || `${response.name || 'response'}-submission`
        );
      }
      flashMessage('All responses downloaded');
    } catch {
      flashError('Failed to download all responses');
    }
  };

  const handleDeleteResponseMedia = async (responseId: number) => {
    const confirmed = await showConfirm(
      'Are you sure you want to delete this response?'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/public-auditions/${responseId}/response`);
      if (selectedAuditionId !== null)
        loadSingleAudition(selectedAuditionId);
      setPublicMessage('Response deleted successfully');
      setTimeout(() => setPublicMessage(''), 2000);
    } catch {
      flashError('Failed to delete response');
    }
  };

  // ── Response inline invite ────────────────────────────────

  const openResponseInvite = (r: any) => {
    setResponseInvites((prev) => ({
      ...prev,
      [r.id]: {
        responseId: r.id,
        userId: r.user_id,
        userName: r.name,
        roleType: 'character',
        selectedCharacter: '',
        crewDescription: '',
        inviteSent: false,
        loading: false,
      },
    }));
  };

  const closeResponseInvite = (responseId: number) => {
    setResponseInvites((prev) => {
      const next = { ...prev };
      delete next[responseId];
      return next;
    });
  };

  const updateResponseInvite = (
    responseId: number,
    patch: Partial<ResponseInviteState>
  ) => {
    setResponseInvites((prev) => ({
      ...prev,
      [responseId]: { ...prev[responseId], ...patch },
    }));
  };

  const handleSendResponseInvite = async (responseId: number) => {
    const state = responseInvites[responseId];
    if (!state || !selectedAuditionId) return;

    let roleName = '';
    let roleDescription: string | null = null;

    if (state.roleType === 'character') {
      if (!state.selectedCharacter) {
        flashError('Please select a character');
        return;
      }
      roleName = state.selectedCharacter;
    } else if (state.roleType === 'crew') {
      if (!state.crewDescription.trim()) {
        flashError('Please select a crew role');
        return;
      }
      roleName = 'Crew';
      roleDescription = state.crewDescription;
    } else {
      roleName = 'Extra';
    }

    updateResponseInvite(responseId, { loading: true });

    try {
      await api.post(
        `/public-auditions/${selectedAuditionId}/invite`,
        {
          receiverId: state.userId,
          roleType: state.roleType,
          roleName,
          roleDescription,
        }
      );

      updateResponseInvite(responseId, { inviteSent: true, loading: false });

      setTimeout(() => closeResponseInvite(responseId), 1800);
    } catch (err: any) {
      updateResponseInvite(responseId, { loading: false });

      if (err.response?.data?.receiverProfileIncomplete) {
        flashError('Receiver profile yet to be completed');
        return;
      }

      if (err.response?.data?.requiresConnection) {
        closeResponseInvite(responseId);
        const confirmed = await showConfirm(
          `You're not connected with ${state.userName}. Send a connection request?`
        );
        if (confirmed) {
          try {
            await api.post('/connections/request', {
              targetUserId: state.userId,
            });
            flashMessage(
              'Connection request sent! Once accepted, you can send the role invitation.'
            );
          } catch (connectErr: any) {
            flashError(
              connectErr.response?.data?.message ||
                'Failed to send connection request'
            );
          }
        }
      } else {
        flashError(
          err.response?.data?.message || 'Failed to send invitation'
        );
      }
    }
  };

  // ── Media modal ───────────────────────────────────────────

  const openMediaModal = (
    url: string,
    type: AuditionPreviewType,
    fileName?: string,
    options?: { mediaId?: number; responseId?: number },
  ) => setMediaModal({ isOpen: true, url, type, fileName, ...options });

  const closeMediaModal = () =>
    setMediaModal({ isOpen: false, url: '', type: 'video' });

  const refreshPublicMediaUrl = useCallback(async (mediaId?: number) => {
    const auditionId = selectedAuditionId ?? draftId;
    if (!mediaId || !auditionId || refreshingPublicMediaRef.current.has(mediaId)) return null;
    refreshingPublicMediaRef.current.add(mediaId);
    try {
      const res = await api.get(`/public-auditions/${auditionId}/media`);
      const nextMedia = res.data?.media || [];
      setPublicMedia(nextMedia);
      const freshItem = nextMedia.find((item: any) => Number(item.id) === Number(mediaId));
      const freshUrl = freshItem?.url || null;
      if (freshUrl) {
        setMediaModal((current) => (
          current.mediaId === mediaId ? { ...current, url: freshUrl } : current
        ));
      }
      return freshUrl;
    } catch (error) {
      console.error('Refresh audition media URL failed', error);
      return null;
    } finally {
      refreshingPublicMediaRef.current.delete(mediaId);
    }
  }, [draftId, selectedAuditionId]);

  const refreshResponseMediaUrl = useCallback(async (responseId?: number) => {
    if (!responseId || refreshingResponseMediaRef.current.has(responseId)) return null;
    refreshingResponseMediaRef.current.add(responseId);
    try {
      const res = await api.get(`/public-auditions/${responseId}/response-media`);
      const freshUrl = res.data?.url || null;
      if (freshUrl) {
        setPublicResponses((current) =>
          current.map((response) => (
            Number(response.id) === Number(responseId) ? { ...response, url: freshUrl } : response
          )),
        );
        setMediaModal((current) => (
          current.responseId === responseId ? { ...current, url: freshUrl } : current
        ));
      }
      return freshUrl;
    } catch (error) {
      console.error('Refresh audition response URL failed', error);
      return null;
    } finally {
      refreshingResponseMediaRef.current.delete(responseId);
    }
  }, []);

  const handleAuditionMediaError = useCallback((mediaId?: number) => {
    void refreshPublicMediaUrl(mediaId);
  }, [refreshPublicMediaUrl]);

  const handleAuditionResponseError = useCallback((responseId?: number) => {
    void refreshResponseMediaUrl(responseId);
  }, [refreshResponseMediaUrl]);

  const handleMediaModalError = useCallback(() => {
    if (mediaModal.responseId) {
      void refreshResponseMediaUrl(mediaModal.responseId);
    } else if (mediaModal.mediaId) {
      void refreshPublicMediaUrl(mediaModal.mediaId);
    }
  }, [mediaModal.mediaId, mediaModal.responseId, refreshPublicMediaUrl, refreshResponseMediaUrl]);

  const handleModalDownload = async () => {
    try {
      const res = await fetch(mediaModal.url);
      if (!res.ok) throw new Error('Fetch failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = mediaModal.fileName || 'media';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(mediaModal.url, '_blank');
    }
  };

  // ── Derived state ─────────────────────────────────────────

  const isExistingAudition = selectedAuditionId !== null;
  const managedPublicAuditions = allPublicAuditions.filter((audition) => audition.id !== draftId);
  const isViewingPublishedAudition = Boolean(
    selectedAuditionId !== null &&
      publicAudition?.id === selectedAuditionId &&
      publicAudition?.is_published
  );
  const isViewingClosedAudition = Boolean(
    selectedAuditionId !== null &&
      publicAudition?.id === selectedAuditionId &&
      !publicAudition?.is_published
  );

  if (loading) {
    return (
      <div className="auditions-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="auditions-container">

      {/* CUSTOM CONFIRM DIALOG */}
      {confirm.open && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p className="confirm-message">{confirm.message}</p>
            <div className="confirm-actions">
              <button className="confirm-btn-ok" onClick={confirm.onConfirm}>
                Okay
              </button>
              <button
                className="confirm-btn-cancel"
                onClick={confirm.onCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="auditions-header">
        <button
          onClick={() => navigate(`/group/${storyId}`)}
          className="back-btn"
        >
          ← Back to Group
        </button>
        <h1>Auditions</h1>
      </div>

      {/* Flash messages */}
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      {publicMessage && (
        <div className="success-message">{publicMessage}</div>
      )}

      {/* Tab bar */}
      <div className="audition-tabs">
        <button
          className={`audition-tab ${activeTab === 'choose' ? 'active' : ''}`}
          onClick={() => setActiveTab('choose')}
        >
          Choose Members
        </button>
        <button
          className={`audition-tab ${activeTab === 'public' ? 'active' : ''}`}
          onClick={() => setActiveTab('public')}
        >
          Public Auditions
        </button>
      </div>

      {/* ════════════════════════════════════════
          CHOOSE MEMBERS TAB
      ════════════════════════════════════════ */}
      {activeTab === 'choose' && (
        <div className="tab-panel">
          <div className="search-filter-section">
            <div className="search-bar-with-filter">
              <input
                type="text"
                placeholder="Search by name or email…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input-auditions"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`filter-toggle-btn ${
                  selectedRoles.length > 0 ? 'active' : ''
                }`}
              >
                Filters{' '}
                {selectedRoles.length > 0 && `(${selectedRoles.length})`}
              </button>
            </div>

            {showFilters && (
              <div className="filters-panel">
                <div className="filters-header">
                  <h3>Filter by Role</h3>
                  {selectedRoles.length > 0 && (
                    <button
                      onClick={handleClearFilters}
                      className="clear-filters-btn"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="roles-filter-grid">
                  {ROLE_OPTIONS.map((role) => (
                    <button
                      key={role}
                      className={`role-filter-btn ${
                        selectedRoles.includes(role) ? 'selected' : ''
                      }`}
                      onClick={() => handleRoleToggle(role)}
                    >
                      {role}
                      {selectedRoles.includes(role) && (
                        <span className="checkmark">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="results-info">
              Showing {filteredUsers.length} of {users.length} users
            </div>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="no-users">
              <p>No users found matching your filters.</p>
              {selectedRoles.length > 0 && (
                <button
                  onClick={handleClearFilters}
                  className="clear-filters-btn-inline"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="users-grid">
              {filteredUsers.map((user) => (
                <div key={user.id} className="user-card-audition">
                  {user.profile_completed === false && (
                    <span className="audition-profile-pending-badge">Yet to complete profile</span>
                  )}
                  <div className="user-avatar-audition">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="user-info-audition">
                    <h3
                      className={user.profile_completed !== false ? 'audition-user-name--clickable' : ''}
                      onClick={() => user.profile_completed !== false && setProfileModalUserId(user.id)}
                    >
                      {user.name}
                    </h3>
                    <p className="user-email">{user.email}</p>
                    {user.roles && user.roles.length > 0 && (
                      <div className="user-roles">
                        {user.roles.slice(0, 3).map((role, i) => (
                          <span key={i} className="role-tag-small">
                            {role}
                          </span>
                        ))}
                        {user.roles.length > 3 && (
                          <span className="role-tag-small more">
                            +{user.roles.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    {user.city && (
                      <p className="user-location">
                        {user.city}
                        {user.state ? `, ${user.state}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="user-actions-audition">
                    {user.profile_completed !== false ? (
                      <>
                        <button
                          onClick={() => handleAddClick(user)}
                          className="add-user-btn"
                        >
                          + Invite
                        </button>
                        {user.groupAssignments &&
                          user.groupAssignments.length > 0 && (
                            <div className="user-group-assignments">
                              <span className="assignments-label">
                                Current Roles
                              </span>
                              {user.groupAssignments.map((a, idx) => (
                                <span
                                  key={idx}
                                  className={`assignment-badge assignment-${a.role_type}`}
                                >
                                  {a.role_type}: {a.role_name}
                                </span>
                              ))}
                            </div>
                          )}
                      </>
                    ) : (
                      <span className="audition-profile-pending-text">Yet to complete profile</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════
          PUBLIC AUDITIONS TAB
      ════════════════════════════════════════ */}
      {activeTab === 'public' && (
        <div className="tab-panel">
          {publicLoading ? (
            <div className="loading">Loading…</div>
          ) : (
            <div className="public-audition-section">

              {/* ── Audition Selector Bar ── */}
              <div className="audition-selector-bar">
                <div className="audition-selector-left">
                  <label className="audition-selector-label">
                    Your Auditions
                  </label>
                  {managedPublicAuditions.length > 0 ? (
                    <select
                      className="audition-selector-dropdown"
                      value={
                        selectedAuditionId === null
                          ? 'new'
                          : String(selectedAuditionId)
                      }
                      onChange={(e) => handleSelectAudition(e.target.value)}
                    >
                      <option value="new">— New Audition —</option>
                      {managedPublicAuditions.map((a) => (
                        <option key={a.id} value={String(a.id)}>
                          {`${a.is_published ? 'Open' : 'Closed'} · ${
                            a.character_line.length > 60
                              ? a.character_line.slice(0, 60) + '…'
                              : a.character_line
                          }`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="audition-selector-empty">
                      No auditions yet
                    </span>
                  )}
                </div>

                {isExistingAudition && (
                  <button
                    className="new-audition-btn"
                    onClick={() => handleSelectAudition('new')}
                  >
                    + New Audition
                  </button>
                )}
              </div>

              {/* ── Form ── */}
              <div className="public-audition-form">
                <div className="public-audition-form-header">
                  <h2>
                    {isExistingAudition
                      ? isEditMode
                        ? 'Edit Audition'
                        : isViewingClosedAudition
                          ? 'Closed Audition'
                          : 'Published Audition'
                      : draftId !== null
                      ? 'New Audition (Draft)'
                      : 'New Public Audition'}
                  </h2>

                  <div className="public-audition-form-header-actions">
                    {isViewingPublishedAudition && !isEditMode && (
                      <span className="published-badge">✓ Published</span>
                    )}
                    {isViewingClosedAudition && !isEditMode && (
                      <span className="draft-badge">Closed</span>
                    )}
                    {isExistingAudition && isEditMode && (
                      <span className="editing-badge">✎ Editing</span>
                    )}
                    {draftId !== null && !isExistingAudition && (
                      <span className="draft-badge">
                        Draft — not yet visible to others
                      </span>
                    )}

                    {isExistingAudition && isOwner && !isEditMode && (
                      <div className="audition-owner-actions">
                        {isViewingPublishedAudition && (
                          <button
                            className="edit-audition-btn"
                            onClick={() => void handleShareAudition()}
                          >
                            Share
                          </button>
                        )}
                        <button
                          className="edit-audition-btn"
                          onClick={handleEnterEditMode}
                        >
                          Edit
                        </button>
                        {isViewingPublishedAudition && (
                          <button
                            className="close-audition-btn"
                            onClick={handleCloseAudition}
                          >
                            Close Audition
                          </button>
                        )}
                        <button
                          className="close-audition-btn"
                          onClick={handleRemoveAudition}
                        >
                          Remove Audition
                        </button>
                      </div>
                    )}

                    {isExistingAudition && isOwner && isEditMode && (
                      <div className="audition-owner-actions">
                        <button
                          className="save-edit-btn"
                          onClick={handleSaveEdit}
                          disabled={isSavingEdit}
                        >
                          {isSavingEdit ? 'Saving…' : 'Save Changes'}
                        </button>
                        <button
                          className="cancel-edit-btn"
                          onClick={handleCancelEdit}
                          disabled={isSavingEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <p className="public-audition-hint">
                  {isViewingClosedAudition && !isEditMode
                    ? 'This audition is closed. It stays here so you can still review every submission.'
                    : isExistingAudition && !isEditMode
                    ? 'This audition is live and visible to all users on the Open Auditions page.'
                    : isExistingAudition && isEditMode
                    ? isViewingClosedAudition
                      ? 'Edit the fields below and save your changes. This audition remains closed while you review responses.'
                      : 'Edit the fields below and save your changes. The audition remains live while you edit.'
                    : 'Fill in the details below. You can upload media before publishing — it will be attached when you save.'}
                </p>

                {/* Fields: edit mode vs view/new */}
                {isExistingAudition && isEditMode ? (
                  <>
                    <div className="form-group">
                      <label>
                        Story Line <span className="required">*</span>
                      </label>
                      <textarea
                        value={editStoryline}
                        onChange={(e) => setEditStoryline(e.target.value)}
                        placeholder="Brief summary of your story…"
                        className="public-audition-textarea"
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Character Line <span className="required">*</span>
                      </label>
                      <textarea
                        value={editCharacterLine}
                        onChange={(e) => setEditCharacterLine(e.target.value)}
                        placeholder="Characters you are looking to cast…"
                        className="public-audition-textarea"
                        rows={3}
                      />
                    </div>

                    <div
                      className="form-group"
                      style={{ marginBottom: '32px' }}
                    >
                      <label>
                        Description <span className="required">*</span>
                      </label>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="What you are looking for in applicants…"
                        className="public-audition-textarea"
                        rows={4}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>
                        Story Line <span className="required">*</span>
                      </label>
                      <textarea
                        value={storyline}
                        onChange={(e) => {
                          setStoryline(e.target.value);
                          ensureDraftExists();
                        }}
                        placeholder="Brief summary of your story…"
                        className="public-audition-textarea"
                        rows={3}
                        disabled={isExistingAudition}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Character Line <span className="required">*</span>
                      </label>
                      <textarea
                        value={characterLine}
                        onChange={(e) => {
                          setCharacterLine(e.target.value);
                          ensureDraftExists();
                        }}
                        placeholder="Characters you are looking to cast…"
                        className="public-audition-textarea"
                        rows={3}
                        disabled={isExistingAudition}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Description <span className="required">*</span>
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => {
                          setDescription(e.target.value);
                          ensureDraftExists();
                        }}
                        placeholder="What you are looking for in applicants…"
                        className="public-audition-textarea"
                        rows={4}
                        disabled={isExistingAudition}
                      />
                    </div>
                  </>
                )}

                {/* Media section */}
                <div className="public-audition-media">
                  <h3>
                    {publicMedia.length > 0
                      ? `Your Media (${publicMedia.length})`
                      : 'Your Media'}
                  </h3>

                  {publicMedia.length > 0 && (
                    <div className="media-grid">
                      {publicMedia.map((m) => (
                        <div key={m.id} className="media-item">
                          <div
                            onClick={() =>
                              openMediaModal(
                                m.url,
                                getAuditionPreviewType(m.media_type, m.file_name, m.url),
                                m.file_name,
                                { mediaId: m.id },
                              )
                            }
                            className="media-preview"
                          >
                            {m.media_type === 'photo' ? (
                              <img src={m.url} alt="" onError={() => handleAuditionMediaError(m.id)} />
                            ) : (
                              <video
                                preload="metadata"
                                muted
                                src={`${m.url}#t=0.5`}
                                onError={() => handleAuditionMediaError(m.id)}
                              >
                              </video>
                            )}
                          </div>
                          {isOwner && (
                            <button
                              onClick={() => handleDeletePublicMedia(m.id)}
                              className="delete-media-btn"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="media-upload-section">
                    <input
                      type="file"
                      accept="video/*,image/*"
                      multiple
                      onChange={handlePublicMediaUpload}
                      className="media-upload-input"
                      id="media-upload"
                      disabled={isUploadingPublicMedia}
                    />
                    <label
                      htmlFor="media-upload"
                      className="media-upload-label"
                    >
                      {isUploadingPublicMedia ? 'Uploading…' : '+ Upload Media'}
                    </label>
                  </div>
                </div>

                {/* Save & Publish — new only */}
                {!isExistingAudition && (
                  <button
                    onClick={handleSavePublicAudition}
                    className="save-public-btn"
                  >
                    Save & Publish
                  </button>
                )}
              </div>

              {/* ── Responses ── */}
              {isExistingAudition && (
                <div className="public-audition-responses">
                  <div className="public-audition-responses-head">
                    <h3>Responses ({publicResponses.length})</h3>
                    {publicResponses.length > 0 && isOwner ? (
                      <button
                        type="button"
                        onClick={() => void handleDownloadAllResponseMedia()}
                        className="download-response-btn download-response-btn--all"
                      >
                        Download All
                      </button>
                    ) : null}
                  </div>
                  {publicResponses.length === 0 ? (
                    <p className="no-responses">No responses yet.</p>
                  ) : (
                    <div className="responses-grid">
                      {publicResponses.map((r) => {
                        const invite = responseInvites[r.id];
                        return (
                          <div key={r.id} className="response-card">
                            <div className="response-user-info">
                              <span
                                className="response-name response-name--clickable"
                                onClick={() =>
                                  setProfileModalUserId(r.user_id)
                                }
                              >
                                {r.name}
                              </span>
                              <span className="response-email">
                                {r.email}
                              </span>
                            </div>

                            {r.url && (
                              <div
                                onClick={() =>
                                  openMediaModal(
                                    r.url,
                                    getAuditionPreviewType(r.media_type, r.file_name, r.url),
                                    r.file_name,
                                    { responseId: r.id },
                                  )
                                }
                                style={{ cursor: 'pointer' }}
                              >
                                {r.media_type === 'photo' ? (
                                  <img
                                    src={r.url}
                                    alt=""
                                    className="response-media"
                                    onError={() => handleAuditionResponseError(r.id)}
                                  />
                                ) : isPdfAuditionAsset(r.media_type, r.file_name, r.url) ? (
                                  <div className="response-media response-media--pdf">
                                    <span>PDF</span>
                                    <small>Open Preview</small>
                                  </div>
                                ) : (
                                  <video
                                    className="response-media"
                                    preload="metadata"
                                    muted
                                    src={`${r.url}#t=0.5`}
                                    onError={() => handleAuditionResponseError(r.id)}
                                  >
                                  </video>
                                )}
                              </div>
                            )}

                            <p className="response-date">
                              {new Date(r.created_at).toLocaleDateString()}
                            </p>

                            {/* ── Owner actions: Download, Delete, Invite — all stacked ── */}
                            {isOwner && (
                              <div className="response-actions-wrapper">
                                <div className="response-actions">
                                  <button
                                    onClick={() =>
                                      handleDownloadResponseMedia(
                                        r.id,
                                        r.file_name
                                      )
                                    }
                                    className="download-response-btn"
                                  >
                                    Download
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteResponseMedia(r.id)
                                    }
                                    className="delete-response-btn"
                                  >
                                    Delete
                                  </button>
                                </div>

                                {/* Invite panel sits below action row, full width */}
                                {!invite ? (
                                  <button
                                    className="invite-role-btn"
                                    onClick={() => openResponseInvite(r)}
                                  >
                                    + Invite to Role
                                  </button>
                                ) : invite.inviteSent ? (
                                  <div className="invite-sent-banner">
                                    ✓ Invitation sent!
                                  </div>
                                ) : (
                                  <div className="response-invite-panel">
                                    {/* Role type tabs */}
                                    <div className="role-type-selector">
                                      {(
                                        [
                                          'character',
                                          'crew',
                                          'extra',
                                        ] as RoleType[]
                                      ).map((type) => (
                                        <button
                                          key={type}
                                          className={`role-type-btn ${
                                            invite.roleType === type
                                              ? 'active'
                                              : ''
                                          }`}
                                          onClick={() =>
                                            updateResponseInvite(r.id, {
                                              roleType: type,
                                              selectedCharacter: '',
                                              crewDescription: '',
                                            })
                                          }
                                          disabled={invite.loading}
                                        >
                                          {type.charAt(0).toUpperCase() +
                                            type.slice(1)}
                                        </button>
                                      ))}
                                    </div>

                                    {/* Role picker */}
                                    {invite.roleType === 'character' && (
                                      <div className="role-input-section">
                                        {characters.length === 0 ? (
                                          <p className="no-characters">
                                            No characters in screenplay
                                          </p>
                                        ) : (
                                          <select
                                            value={invite.selectedCharacter}
                                            onChange={(e) =>
                                              updateResponseInvite(r.id, {
                                                selectedCharacter:
                                                  e.target.value,
                                              })
                                            }
                                            className="character-select"
                                            disabled={invite.loading}
                                          >
                                            <option value="">
                                              — Select Character —
                                            </option>
                                            {characters.map((char, i) => (
                                              <option
                                                key={i}
                                                value={
                                                  typeof char === 'string'
                                                    ? char
                                                    : char.character_name
                                                }
                                              >
                                                {typeof char === 'string'
                                                  ? char
                                                  : char.character_name}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                      </div>
                                    )}

                                    {invite.roleType === 'crew' && (
                                      <div className="role-input-section">
                                        <select
                                          value={invite.crewDescription}
                                          onChange={(e) =>
                                            updateResponseInvite(r.id, {
                                              crewDescription: e.target.value,
                                            })
                                          }
                                          className="crew-role-select"
                                          disabled={invite.loading}
                                        >
                                          <option value="">
                                            — Select Crew Role —
                                          </option>
                                          {ROLE_OPTIONS.map((role, i) => (
                                            <option key={i} value={role}>
                                              {role}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    )}

                                    {invite.roleType === 'extra' && (
                                      <div className="role-input-section">
                                        <p className="extra-info">
                                          Invite as a background actor.
                                        </p>
                                      </div>
                                    )}

                                    {/* Invite actions */}
                                    <div className="modal-actions">
                                      <button
                                        className="send-invitation-btn"
                                        onClick={() =>
                                          handleSendResponseInvite(r.id)
                                        }
                                        disabled={invite.loading}
                                      >
                                        {invite.loading
                                          ? 'Sending…'
                                          : 'Send Invitation'}
                                      </button>
                                      <button
                                        className="cancel-modal-btn"
                                        onClick={() =>
                                          closeResponseInvite(r.id)
                                        }
                                        disabled={invite.loading}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════
          ROLE SELECTION MODAL (Choose Members)
      ════════════════════════════════════════ */}
      {showRoleModal && selectedUser && (
        <div
          className="modal-overlay"
          onClick={() => setShowRoleModal(false)}
        >
          <div
            className="modal-content-audition"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowRoleModal(false)}
              className="modal-close"
            >
              ✕
            </button>

            <h2>Select Role for {selectedUser.name}</h2>

            {selectedUser.groupAssignments &&
              selectedUser.groupAssignments.length > 0 && (
                <div className="current-assignments-in-modal">
                  <p className="assignments-modal-label">
                    Already assigned in this group:
                  </p>
                  <div className="assignments-modal-list">
                    {selectedUser.groupAssignments.map((a, idx) => (
                      <span
                        key={idx}
                        className={`assignment-badge assignment-${a.role_type} ${
                          a.status === 'pending' ? 'assignment-pending' : ''
                        }`}
                      >
                        {a.role_type}: {a.role_name}
                        {a.status === 'pending' && <em> (pending)</em>}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            {inviteSent && (
              <div className="invite-sent-banner">
                ✓ Invitation sent! {selectedUser.name} has been notified.
              </div>
            )}

            <div className="role-type-selector">
              {(['character', 'crew', 'extra'] as RoleType[]).map((type) => (
                <button
                  key={type}
                  className={`role-type-btn ${
                    roleType === type ? 'active' : ''
                  }`}
                  onClick={() => setRoleType(type)}
                  disabled={inviteSent}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {roleType === 'character' && (
              <div className="role-input-section">
                <label>Select Character</label>
                {characters.length === 0 ? (
                  <p className="no-characters">
                    No characters found in screenplay
                  </p>
                ) : (
                  <select
                    value={selectedCharacter}
                    onChange={(e) => setSelectedCharacter(e.target.value)}
                    className="character-select"
                    disabled={inviteSent}
                  >
                    <option value="">— Select Character —</option>
                    {characters.map((char, i) => (
                      <option
                        key={i}
                        value={
                          typeof char === 'string'
                            ? char
                            : char.character_name
                        }
                      >
                        {typeof char === 'string'
                          ? char
                          : char.character_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {roleType === 'crew' && (
              <div className="role-input-section">
                <label>Select Crew Role</label>
                <select
                  value={crewDescription}
                  onChange={(e) => setCrewDescription(e.target.value)}
                  className="crew-role-select"
                  disabled={inviteSent}
                >
                  <option value="">— Select Crew Role —</option>
                  {ROLE_OPTIONS.map((role, i) => (
                    <option key={i} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {roleType === 'extra' && (
              <div className="role-input-section">
                <p className="extra-info">
                  This user will be invited as an extra (background actor).
                </p>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={handleSendInvitation}
                className="send-invitation-btn"
                disabled={inviteSent}
              >
                {inviteSent ? 'Sent!' : 'Send Invitation'}
              </button>
              <button
                onClick={() => setShowRoleModal(false)}
                className="cancel-modal-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          MEDIA PREVIEW MODAL
      ════════════════════════════════════════ */}
      {mediaModal.isOpen && (
        <div className="modal-overlay" onClick={closeMediaModal}>
          <div
            className="media-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={closeMediaModal} className="modal-close-large">
              ✕
            </button>

            {mediaModal.type === 'video' ? (
              <video
                controls
                autoPlay
                className="modal-media"
                src={mediaModal.url}
                onError={handleMediaModalError}
              >
                Your browser does not support the video tag.
              </video>
            ) : mediaModal.type === 'pdf' ? (
              <iframe
                src={mediaModal.url}
                title="PDF Preview"
                className="modal-media modal-media--pdf"
                onError={handleMediaModalError}
              />
            ) : (
              <img
                src={mediaModal.url}
                alt=""
                className="modal-media"
                onError={handleMediaModalError}
              />
            )}

            <button
              onClick={handleModalDownload}
              className="modal-download-btn"
            >
              Download
            </button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          USER PROFILE MODAL
      ════════════════════════════════════════ */}
      {profileModalUserId && (
        <UserProfileModal
          userId={profileModalUserId}
          onClose={() => setProfileModalUserId(null)}
          onConnect={async (uid) => {
            try {
              const targetUser = users.find(user => user.id === uid);
              if (targetUser && !targetUser.profile_completed) {
                flashError('Receiver profile yet to be completed');
                return;
              }
              await api.post('/connections/request', {
                targetUserId: uid,
              });
              flashMessage('Connection request sent!');
            } catch (err: any) {
              flashError(
                err.response?.data?.message ||
                  'Failed to send connection request'
              );
            }
          }}
          onDisconnect={async (uid) => {
            try {
              await api.delete(`/connections/${uid}`);
              setUsers((current) =>
                sortUsersByProfileCompletion(
                  current.map((entry) =>
                    entry.id === uid
                      ? { ...entry, connectionStatus: null, connectionInitiatedBy: null }
                      : entry,
                  ),
                ),
              );
              flashMessage('Disconnected successfully!');
            } catch (err: any) {
              flashError(err.response?.data?.message || 'Failed to disconnect');
              throw err;
            }
          }}
          onStartChat={(uid) => navigate(`/direct-messages/${uid}`)}
        />
      )}
    </div>
  );
};

export default AuditionsPage;
